'use client'

import type { KanjiData } from '@/kanjivg/types'

interface Props {
    kanjiData: KanjiData
    viewBox: string
}

/**
 * Ghost stroke layer overlaid on the user's drawing after a grader reject (§5.0.4).
 *
 * Renders the KanjiVG canonical paths at opacity 0.4, body-fg ink
 * (stroke="currentColor" + className="text-body-fg"), no fill, no vermilion.
 * The parent unmounts this component on advance so no cleanup is needed.
 *
 * `kanjiData` and `viewBox` are both passed in from the draw-canvas parent,
 * which already holds the enriched useKanji() result it needs for grading —
 * this overlay reuses that object instead of calling useKanji a second
 * time. DrawCanvas only mounts this component once its own `kanjiData` is
 * non-null (the `kanjiData &&` guard around the render site), so the
 * enriched data is always present here.
 */
export function CanonicalOverlay({ kanjiData, viewBox }: Props) {
    return (
        <svg
            aria-hidden
            viewBox={viewBox}
            className="pointer-events-none absolute inset-0 size-full text-body-fg"
        >
            {kanjiData.strokePaths.map((d, i) => (
                <path
                    key={i}
                    d={d}
                    stroke="currentColor"
                    strokeWidth="3"
                    fill="none"
                    opacity={0.4}
                />
            ))}
        </svg>
    )
}
